/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */
import React from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { StaticQuery, graphql } from 'gatsby';

const defaultLangKey = 'en-ca';

function SEO({
  description,
  lang,
  meta,
  title,
  img,
  location,
  noIndex,
  canonical,
}) {
  let path = location ? location.href : '';
  // * Lang is the html-lang of the content on the page
  // * lang is the language that the page should be associated with
  // * if lang and lang dont match, that means we are on a translated paged without translated copy, i.e the copy duplicates that of the main page, so we set a canonical link

  return (
    <StaticQuery
      query={graphql`
        query {
          site {
            siteMetadata {
              title
              description
              author
              siteUrl
            }
          }
          prodigy {
            localeMappings {
              path
              locale
            }
          }
        }
      `}
      render={({ site, prodigy }) => {
        const metaDescription = description || site.siteMetadata.description;

        let canonicalPath = path;
        let alternativeVersions = [];

        if (location) {
          if (!path) path = `${site.siteMetadata.siteUrl}${location.pathname}`;
          // * Get the current custom locale path on the page
          const currentPrefix = prodigy.localeMappings.find(
            localeData => localeData.locale === lang
          );
          // * Get the current custom locale path for the canonical Page
          const canonicalPrefix = prodigy.localeMappings.find(
            localeData => localeData.locale === defaultLangKey
          );
          // * Replace the current custom locale with the canonical one
          canonicalPath = path.replace(
            currentPrefix.path,
            canonicalPrefix.path
          );
          // * Returns an array of alternative page links, based on all locales available
          alternativeVersions = prodigy.localeMappings.map(cur => {
            if (cur.locale !== lang) {
              const alternatePath = path.replace(currentPrefix.path, cur.path);
              return {
                rel: 'alternate',
                href: alternatePath,
                hreflang: `${cur.locale}`,
              };
            }
            return {
              rel: 'alternate',
              href: path,
              hreflang: `${lang}`,
            };
          });
        }

        return (
          <Helmet
            htmlAttributes={{
              lang,
            }}
            title={title}
            titleTemplate={`%s | ${site.siteMetadata.title}`}
            link={[...alternativeVersions].concat(
              canonical
                ? {
                    rel: 'canonical',
                    href: canonicalPath,
                  }
                : {}
            )}
            meta={[
              {
                name: `description`,
                content: metaDescription,
              },
              {
                property: `og:title`,
                content: title,
              },
              {
                property: `og:description`,
                content: metaDescription,
              },
              {
                property: `og:type`,
                content: `website`,
              },
              {
                name: `twitter:card`,
                content: `summary`,
              },
              {
                name: `twitter:creator`,
                content: site.siteMetadata.author,
              },
              {
                name: `twitter:title`,
                content: title,
              },
              {
                name: `twitter:description`,
                content: metaDescription,
              },
            ]
              .concat(meta)
              .concat(
                img
                  ? {
                      property: `og:image`,
                      content: img.url,
                    }
                  : {}
              )
              .concat(
                noIndex ? { name: 'robots', content: 'noindex, nofollow' } : {}
              )}
          />
        );
      }}
    />
  );
}

SEO.defaultProps = {
  lang: `en-ca`,
  meta: [],
  description: ``,
  location: null,
  noIndex: null,
  canonical: false,
  img: { url: 'https://webcdn.prodigygame.com/common/images/socialshare.png' },
};

SEO.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired,
  location: PropTypes.shape(),
  noIndex: PropTypes.bool,
  canonical: PropTypes.bool,
  img: PropTypes.shape(),
};

export default SEO;
